// The capability names the backend enforces, mirrored from the catalog in
// internal/platform/auth/registry.go. This is the ONE place the strings live on
// the client: typing them as a union means a gate written against a capability
// that no route enforces is a compile error, not a silently-open screen.
//
// The server remains the real boundary (every route is gated by Require(perm),
// §6.3). Everything here decides what to RENDER: a client-side check that a
// determined user can bypass by editing the bundle, which is exactly why the
// server never trusts it.
export type Capability =
  | "jobcard:read"
  | "jobcard:create"
  | "jobcard:update"
  | "jobcard:update_status"
  | "invoice:read"
  | "invoice:write"
  | "invoice:issue"
  | "invoice:credit"
  | "payment:record"
  | "payment:refund"
  | "payment:reverse"
  | "customer:read"
  | "customer:write"
  | "inventory:read"
  | "inventory:manage"
  | "inventory:adjust"
  | "supplier:read"
  | "supplier:write"
  | "po:read"
  | "po:write"
  | "po:receive"
  | "payable:pay"
  | "report:read"
  | "accounting:read"
  | "accounting:post"
  | "accounting:manage"
  | "expense:read"
  | "expense:record"
  | "expense:manage"
  | "payroll:read"
  | "payroll:run"
  | "payroll:manage"
  | "profit:read"
  | "tenant:manage"
  | "branch:read"
  | "branch:manage"
  | "role:manage"
  | "user:manage"
  | "audit:read"
  | "apikey:read"
  | "apikey:manage"
  | "agent:enroll"
  | "printer:read"
  | "printer:print"
  | "printer:manage"
  | "notification:read"
  | "notification:manage"
  // Role-level marker, not an action: a role carrying it cannot sign in at all
  // (§6.3). Listed so the roles editor can name it; never gate a screen on it.
  | "auth:no_login";

/** authkit's wildcard: a role holding it passes every check. */
export const PERM_ALL = "*";

/**
 * What a screen or control needs. An array means ANY-of, not all-of: a page a
 * user can reach two ways (procurement, reachable with either po:read or
 * supplier:read) states both, and holding one is enough. Require all-of by
 * calling can() twice - it reads better than a flag would.
 */
export type CapabilityNeed = Capability | readonly Capability[];

/**
 * Does this capability list satisfy need? Fails CLOSED: an absent list (the
 * response predates the field, or /auth/me has not resolved) grants nothing,
 * because the alternative is briefly showing a cashier the payroll screen.
 *
 * `need` is plain strings here, not the tenant union, because the PLATFORM axis
 * (6.6) reuses this one function with its own capability names - the wildcard
 * rule is identical and having two copies of it is how the two axes drift apart.
 * The typing that stops a made-up capability lives at the hooks that call it:
 * useAuth().can takes CapabilityNeed, usePlatformAdmin().can takes the platform
 * union.
 */
export function can(
  caps: readonly string[] | undefined,
  need: string | readonly string[],
): boolean {
  if (!caps || caps.length === 0) return false;
  if (caps.includes(PERM_ALL)) return true;
  return typeof need === "string" ? caps.includes(need) : need.some((n) => caps.includes(n));
}
